import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { motion, AnimatePresence } from 'framer-motion'

// Actions
import { actions as userSongActions } from 'modules/usersongs/actions'

// Components
import { Checkbox, Button, EmptyState, IconDownloadCloud, IconCheck, Toast } from '@tracklib/kit'
import InfoBox from 'components/usersong/license/infobox'
import PageHeader from 'components/usersong/pageheader'
import RightsholdersTable from 'components/usersong/rightsholderstable'
import SuccessMessage from 'components/usersong/license/successmessage'
import { getSharingStatusString } from 'components/usersongs/song/status'

// Hooks
import { useProfile } from 'modules/profile/hooks'
import { useRequest } from 'modules/requests/hooks'

// Selectors
import { selectUserSongSteps, selectUserSongStepsCompleted } from 'modules/usersongs/selectors'

// Types
import { UserSong, Profile } from 'types'

// Utils
import formatDate from 'lib/formatdate'
import styles from './license.css'

const transition = {
  y: { type: 'spring', stiffness: 500, mass: 0.5, damping: 20 },
  opacity: { duration: 0.1 },
}

const successMessageTransitionDuration = 500

const renderInfoBoxItems = (userSong: UserSong, id: number) => {
  const { rightsholders } = userSong
  const worksRegistration = userSong.work_registration_service || {}
  const releaseRegistration = userSong.release_registration_service || {}

  switch (id) {
    case 1:
      return (
        <>
          <div className="col">
            <InfoBox.Item label="Final song title">{userSong.song_title}</InfoBox.Item>
            <InfoBox.Item label="Artist(s)">{userSong.artist_name}</InfoBox.Item>
          </div>
          <div className="col">
            <InfoBox.Item label="Song duration">{userSong.runtime}</InfoBox.Item>
            <InfoBox.Item label="Audio file" audio>
              {userSong.audio_file_name}
            </InfoBox.Item>
          </div>
        </>
      )

    case 2:
      return (
        <>
          {userSong.rightsholders.length > 0 && (
            <InfoBox.Item label="">
              <RightsholdersTable rightsholders={rightsholders} />
            </InfoBox.Item>
          )}
        </>
      )

    case 3:
      return (
        <div className="col">
          <InfoBox.Item label="Registration guide">{worksRegistration.name}</InfoBox.Item>
          <InfoBox.Item label="ISWC">{userSong.iswc}</InfoBox.Item>
        </div>
      )

    case 4:
      return <InfoBox.Item label="Status">Registered</InfoBox.Item>

    case 5:
      return (
        <div className="col">
          <InfoBox.Item label="Distribution service">
            {userSong.custom_release_registration_service || releaseRegistration.name}
          </InfoBox.Item>
          <InfoBox.Item label="ISRC">{userSong.isrc}</InfoBox.Item>
        </div>
      )
    default:
      return null
  }
}

const getLicenseActivationDateText = (unlicensed: boolean, license_activation_date: string): string => {
  if (unlicensed) {
    return 'Unlicensed'
  }
  if (license_activation_date) {
    return formatDate(license_activation_date)
  }
  return 'Not yet activated'
}

const SummaryItem = ({ title, text }: { title: string; text: string }) => (
  <div className={styles.summaryItem}>
    <span className={styles.itemTitle}>{title}</span>
    <span className={styles.itemText}>{text}</span>
  </div>
)

const ActivationConfirmationSteps = ({ profile, song }: { profile: Profile; song: UserSong }) => {
  return (
    <>
      <div>
        <strong>By confirming you agree that you have complied to the following:</strong>
      </div>
      <br />
      <ul className={styles.activationStepsList}>
        <li>I have provided Tracklib with the final title, length, and song genre</li>
        <li>
          The final song title is not the same as, or too similar to <strong>&quot;{song.samples[0].song.title}&quot;</strong>
        </li>
        <li>The works and recording registration of the new song are done and valid</li>
        <li>I have released this song and provided Tracklib with the right ISRC</li>
        <li>
          <strong>
            My email address is <em>{profile.email}</em> and my phone number is <em>{profile.phone_number}</em>
          </strong>
        </li>
        <li>
          <strong>I understand that I now need to report any revenue to Tracklib twice a year.</strong> For more info on this, click{' '}
          <a
            style={{ textDecoration: 'underline' }}
            rel="noreferrer"
            target="_blank"
            href="https://support.tracklib.com/hc/en-us/sections/360003987479-License-accounting"
          >
            here
          </a>
        </li>
      </ul>
    </>
  )
}

type LicenseProps = {
  userSong: UserSong
}

const License = ({ userSong }: LicenseProps): JSX.Element => {
  const dispatch = useDispatch()
  const profile = useProfile()
  const activateUserSongLicenseRequest = useRequest('ACTIVATE_USER_SONG')
  const [confirmChecked, setConfirmChecked] = useState(false)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  const { id, state, license_activation_date, licensee, samples } = userSong
  const userSongUnlicensed = state === 'UNLICENSED'
  const isFucked = state === 'INVALID' || state === 'TERMINATED'
  const isLocked = state === 'ACTIVE' || isFucked
  const paymentPending = userSong.samples.some(sample => sample.payment_status !== 'PAID')
  const steps = selectUserSongSteps(userSong)
  const { releaseSongCompleted } = selectUserSongStepsCompleted(userSong)
  const unlicensed = state === 'UNLICENSED'
  const activateUserSongLicense = (userSongId: number) => dispatch(userSongActions.activateUserSongLicense(userSongId))
  const handleConfirm = () => {
    activateUserSongLicense(id).then(() => {
      setShowSuccessMessage(true)
      setTimeout(() => window.scroll(0, 0), successMessageTransitionDuration + 100)
    })
  }

  return (
    <>
      {isLocked && (
        <PageHeader
          title="License details"
          subtitle="This is the full license information as filled in by you. This information is also in your final license, which can be downloaded below:"
        />
      )}
      {userSongUnlicensed ? (
        <EmptyState
          text="Nothing to see or do here - yet"
          subtitle="After you license your song we ask you to complete the license by filling out important information connected to your song for when releasing. License and see you again!"
          emoji="looking"
        />
      ) : (
        <>
          {!isLocked && (
            <PageHeader
              title="License"
              subtitle="Before you have a completed license from Tracklib the new song is for private use only - it cannot be distributed or
                monetized. Fill in the appropriate data below to activate the license. We will try to guide you through each step."
            />
          )}
          <div className={styles.actions}>
            <div className={styles.downloadButtonWrapper}>
              <Button
                variant="outline"
                className={styles.downloadButton}
                disabled={isFucked}
                href={
                  isLocked
                    ? `/samplelicense/${profile.download_token}/${samples[0].license_order}.pdf`
                    : `/licensepreview/download/${profile.download_token}/${id}/`
                }
                target="_blank"
              >
                <IconDownloadCloud />
                Download License
              </Button>
              {!isFucked &&
                (isLocked ? 'Your license is complete' : 'Your license is currently incomplete but can be used for distributors etc.')}
            </div>
          </div>
          <div className={styles.licenceSummaryWrapper}>
            <div className={styles.licenceSummary}>
              <SummaryItem title="Sharing status" text={getSharingStatusString(state)} />
              <SummaryItem title="Licensee" text={unlicensed ? 'Unlicensed' : licensee} />
              <SummaryItem title="License activation date" text={getLicenseActivationDateText(unlicensed, license_activation_date)} />
              <SummaryItem title="Additional Contact Details" text={`${profile.full_name} (${profile.email})`} />
            </div>
          </div>
          <hr className={styles.divider} />
          <div className={styles.licenceHeader}>
            <div className={styles.heading}>{isLocked ? 'Completed license details' : 'Complete your license details'}</div>
          </div>
          {steps.map(step => {
            const prevStep = steps.find(x => x.id === step.id - 1)
            return (
              <InfoBox
                key={step.id}
                step={step.id}
                modal={step.modal}
                userSongId={userSong.id}
                title={step.name}
                subtitle={step.description}
                buttonText={step.cta}
                completed={step.completed}
                disabled={prevStep && !prevStep.completed}
                isLocked={isLocked}
                bodyClassName={isLocked && step.id === 5 ? styles.lastBox : ''}
              >
                {renderInfoBoxItems(userSong, step.id)}
              </InfoBox>
            )
          })}
          {!isLocked && (
            <InfoBox
              id="activate"
              userSongId={id}
              title="Activate your license"
              subtitle="This is the final step that will activate your license. Once it is active, you are obliged to come back and report revenue for the song twice a year*. We will remind you when it’s time, so make sure you update your contact information regularly so we are able to reach you."
              showChildrenWhenNotCompleted
              disabled={!releaseSongCompleted || paymentPending}
              step={<IconCheck className={styles.iconCheck} />}
              bodyClassName={styles.lastBox}
              isLocked={isLocked}
            >
              <ActivationConfirmationSteps profile={profile} song={userSong} />
              {paymentPending ? (
                <Toast
                  title="License payment pending"
                  description="License cannot be activated since your license payment is pending"
                  className={styles.checkbox}
                />
              ) : (
                <Checkbox
                  value={confirmChecked}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmChecked(e.target.checked)}
                  name="license_ready"
                  size="large"
                  type="yellow"
                  bordered
                  disabled={!releaseSongCompleted}
                  className={styles.checkbox}
                  datatesting="activate_license_checkbox"
                >
                  I confirm that all information above is final and correct
                </Checkbox>
              )}
              <AnimatePresence>
                {confirmChecked && (
                  <motion.div
                    animate={{ y: 0, opacity: 1, transition }}
                    exit={{ y: -10, opacity: 0, transition }}
                    initial={{
                      y: -10,
                      opacity: 0,
                      transition,
                    }}
                    key="buttonwrapper"
                  >
                    <Button
                      className={styles.confirmButton}
                      disabled={activateUserSongLicenseRequest.isLoading || !releaseSongCompleted || !confirmChecked}
                      isLoading={activateUserSongLicenseRequest.isLoading}
                      onClick={() => handleConfirm()}
                      datatesting="button_activate_license"
                    >
                      Activate license
                    </Button>
                  </motion.div>
                )}
              </AnimatePresence>
            </InfoBox>
          )}
          <SuccessMessage
            isOpen={showSuccessMessage}
            close={() => setShowSuccessMessage(false)}
            title="Your license is active!"
            subtitle="Let’s party! 🎉 You will need to report revenue every April and October to make sure your license stays valid. We’ll email you a reminder when it’s time!"
            buttonText="Got it!"
            transitionDuration={successMessageTransitionDuration * 0.001}
          />
        </>
      )}
    </>
  )
}

export default License
